import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import cloneDeep from 'lodash.clonedeep';
import Autocomplete from '@mui/lab/Autocomplete';
import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import { UNITS } from '../../constants';

const useStyles = makeStyles()(() => ({
    halfWidthInput: {
        width: '50%',
    },
}));

const ALLOWED_AMOUNT_CHARS = /^([1-9][0-9]*? )?[1-9][0-9]*\/[1-9][0-9]*$/m;

export default function IngredientDialog({
    open,
    handleClose,
    editIndex = null,
    ingredients,
    setIngredients
}: any) {
    const isUpdate = editIndex != null && editIndex >= 0;
    const initialIngredient = isUpdate ? ingredients[editIndex] : {};
    const [ingredient, setIngredient] = useState(initialIngredient);
    const unitRef = useRef(false);
    const nameRef = useRef(false);
    const infoRef = useRef(false);

    useEffect(() => {
        setIngredient(initialIngredient);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, editIndex]);

    const { classes } = useStyles();

    function addIngredient() {
        setIngredients([
            ...ingredients,
            { ...ingredient,
                amount: ingredient.amount ? ingredient.amount.trim() : '',
                name: ingredient.name.trim(),
                unit: ingredient.unit ? ingredient.unit.trim() : '',
                info: ingredient.info ? ingredient.info.trim() : '',
            },
        ]);
        handleClose();
    }

    function updateIngredient() {
        const updateIngredients = cloneDeep(ingredients);
        updateIngredients[editIndex] = {
            ...ingredient,
            amount: ingredient.amount ? ingredient.amount.trim() : '',
            name: ingredient.name.trim(),
            unit: ingredient.unit ? ingredient.unit.trim() : '',
            info: ingredient.info ? ingredient.info.trim() : '',
        };
        setIngredients(updateIngredients);
        handleClose();
    }

    function setPendingIngredient(change: any) {
        setIngredient({ ...ingredient, ...change });
    }

    function keyPressed(e: any, nextFieldRef: any) {
        if (e.charCode === 13) {
            nextFieldRef.current.focus();
            e.preventDefault();
        }
    }

    const amountValid = !ingredient.amount || ALLOWED_AMOUNT_CHARS.test(ingredient.amount)
        || (!Number.isNaN(Number(ingredient.amount)) && Number.parseFloat(ingredient.amount) > 0);
    const ingredientValid = ingredient.name && amountValid;

    return (
        <Dialog open={open} onClose={() => handleClose()} aria-labelledby="form-dialog-title">
            <DialogTitle id="form-dialog-title">{isUpdate ? 'Edit Ingredient' : 'New Ingredient'}</DialogTitle>
            <DialogContent>
                <Typography gutterBottom variant="body1">
                    Enter the ingredient details
                </Typography>
                <Box style={{ display: 'flex' }}>
                    <TextField
                        autoFocus
                        className={classes.halfWidthInput}
                        margin="dense"
                        id="amount"
                        label="Amount"
                        variant="outlined"
                        value={ingredient.amount}
                        onChange={(event: any) => setPendingIngredient({ amount: event.target.value })}
                        InputProps={{ onKeyPress: (e: any) => keyPressed(e, unitRef) }}
                        error={!amountValid}
                    />
                    &nbsp;
                    <Autocomplete
                        className={classes.halfWidthInput}
                        options={UNITS}
                        freeSolo
                        value={ingredient.unit}
                        inputValue={ingredient.unit}
                        onInputChange={(_: any, unit: any) => setPendingIngredient({ unit })}
                        renderInput={(params: any) => <TextField
                            {...params}
                            inputRef={unitRef}
                            style={{ width: '100%' }}
                            margin="dense"
                            label="Unit"
                            variant="outlined"
                            InputProps={{ onKeyPress: (e: any) => keyPressed(e, nameRef) }}
                        />}
                    />
                </Box>
                <TextField
                    inputRef={nameRef}
                    required
                    margin="dense"
                    id="ingredient-name"
                    label="Ingredient Name"
                    variant="outlined"
                    fullWidth
                    value={ingredient.name || ''}
                    onChange={(event: any) => setPendingIngredient({ name: event.target.value.replace(/\s\s+/g, ' ') })}
                    error={!ingredient.name}
                    InputProps={{ onKeyPress: (e: any) => keyPressed(e, infoRef) }}
                />
                <TextField
                    inputRef={infoRef}
                    margin="dense"
                    id="info"
                    label="Info"
                    variant="outlined"
                    fullWidth
                    value={ingredient.info || ''}
                    onChange={(event: any) => setPendingIngredient({ info: event.target.value.replace(/\s\s+/g, ' ') })}
                    helperText="e.g finely chopped, beaten, sliced"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => handleClose()}>
                    Cancel
                </Button>
                {isUpdate
                    ? (
                        <Button disabled={!ingredientValid} onClick={() => updateIngredient()} variant="contained" color="primary">
                            Update
                        </Button>
                    )
                    : (
                        <Button disabled={!ingredientValid} onClick={() => addIngredient()} variant="contained" color="primary">
                            Add
                        </Button>
                    )}
            </DialogActions>
        </Dialog>
    );
}
